/**
 * apps/admin/src/components/charts — hand-rolled SVG. **No charting dependency**
 * (§14.8).
 *
 * Four chart types are needed across the V2 screens and each is a few dozen
 * lines; a chart library is 100–300 KB of JavaScript and a supply-chain surface
 * on a government host, bought to draw rectangles.
 *
 * ===========================================================================
 * COLOUR COMES FROM THE TOKEN PRESET, AND EVERY CLASS NAME IS A LITERAL.
 * ===========================================================================
 *
 * There is not one hex value in this file: the palette below is
 * `@navoiy/tokens`, the same source the badges and buttons read, so a bar and a
 * badge for the same status are the same colour by construction.
 *
 * Each entry spells out its `fill`, `stroke` and `bg` class IN FULL rather than
 * deriving one from another at runtime. Tailwind's JIT compiler finds classes
 * by scanning this file's TEXT — a `className.replace('fill-', 'bg-')` produces
 * a class that exists in the DOM and in no stylesheet, and the failure is a
 * silently colourless chart rather than a build error.
 *
 * ===========================================================================
 * ACCESSIBILITY, WHICH FOR A CHART MEANS: THE NUMBERS ARE ALSO TEXT.
 * ===========================================================================
 *
 * Every chart is `role="img"` with a caller-supplied label, every series
 * carries a `<title>`, and every one of them ships with a legend or a table
 * carrying the same figures. A staff member reading with a screen reader, or
 * reading a printout, loses nothing but the shape.
 */
import type { ReactNode } from 'react';

export interface ChartColor {
  readonly fill: string;
  readonly stroke: string;
  readonly bg: string;
}

/** The categorical ramp, in the order a chart consumes it. */
export const SERIES_COLORS: readonly ChartColor[] = [
  { fill: 'fill-brand-600', stroke: 'stroke-brand-600', bg: 'bg-brand-600' },
  { fill: 'fill-gold-500', stroke: 'stroke-gold-500', bg: 'bg-gold-500' },
  { fill: 'fill-success-600', stroke: 'stroke-success-600', bg: 'bg-success-600' },
  { fill: 'fill-brand-300', stroke: 'stroke-brand-300', bg: 'bg-brand-300' },
  { fill: 'fill-danger-500', stroke: 'stroke-danger-500', bg: 'bg-danger-500' },
  { fill: 'fill-ink-400', stroke: 'stroke-ink-400', bg: 'bg-ink-400' },
];

/** The four lifecycle statuses, coloured to match `Badge` on the inbox. */
export const STATUS_COLOR: Readonly<Record<string, ChartColor>> = {
  new: { fill: 'fill-gold-500', stroke: 'stroke-gold-500', bg: 'bg-gold-500' },
  in_review: { fill: 'fill-brand-600', stroke: 'stroke-brand-600', bg: 'bg-brand-600' },
  answered: { fill: 'fill-success-600', stroke: 'stroke-success-600', bg: 'bg-success-600' },
  closed: { fill: 'fill-ink-400', stroke: 'stroke-ink-400', bg: 'bg-ink-400' },
};

export const RECEIVED_COLOR: ChartColor = SERIES_COLORS[0]!;
export const CLOSED_COLOR: ChartColor = SERIES_COLORS[2]!;

export interface Slice {
  key: string;
  label: string;
  value: number;
  color?: ChartColor;
}

function colorOf(slice: Slice, index: number): ChartColor {
  return slice.color ?? SERIES_COLORS[index % SERIES_COLORS.length]!;
}

function ChartFrame({
  label,
  children,
  className = '',
}: {
  label: string;
  children: ReactNode;
  className?: string;
}): React.JSX.Element {
  return (
    <figure role="img" aria-label={label} className={className}>
      {children}
    </figure>
  );
}

function EmptyChart({ message }: { message: string }): React.JSX.Element {
  return <p className="py-6 text-center text-sm text-ink-500">{message}</p>;
}

// ---------------------------------------------------------------------------
// Horizontal bars — the all-mahalla chart (§14.8 screens 1 and 6)
// ---------------------------------------------------------------------------

/**
 * One row per category.
 *
 * Drawn as HTML rather than SVG on purpose: a horizontal bar chart is a list of
 * labelled proportions, and a `div` with a width percentage reflows, truncates
 * its labels and stays legible at any container width without a viewBox to
 * fight. Geometry is what the donut and the sparkline below need; this needs
 * layout.
 *
 * **Every category is rendered, including the zeros.** `/admin/stats/mahallas`
 * LEFT JOINs from `mahallas` precisely so a mahalla with no appeals arrives as
 * a zero row (§14.2.2); dropping it here would undo that at the last step and
 * make a silent mahalla indistinguishable from one that does not exist.
 */
export function BarChart({
  label,
  items,
  emptyLabel,
  max: explicitMax,
}: {
  label: string;
  items: readonly Slice[];
  emptyLabel: string;
  max?: number;
}): React.JSX.Element {
  if (items.length === 0) return <EmptyChart message={emptyLabel} />;
  const max = explicitMax ?? Math.max(...items.map((item) => item.value), 0);

  return (
    <ChartFrame label={label} className="flex flex-col gap-2">
      {items.map((item, index) => (
        <div
          key={item.key}
          className="grid grid-cols-[minmax(6rem,11rem)_1fr_auto] items-center gap-3"
        >
          <span className="truncate text-sm text-ink-700" title={item.label}>
            {item.label}
          </span>
          <span className="h-3 rounded-chip bg-surface-100">
            <span
              className={`block h-3 rounded-chip ${colorOf(item, index).bg}`}
              style={{ width: max > 0 ? `${(item.value / max) * 100}%` : '0%' }}
            />
          </span>
          <span className="w-12 text-right text-sm font-semibold tabular-nums text-ink-900">
            {item.value}
          </span>
        </div>
      ))}
    </ChartFrame>
  );
}

// ---------------------------------------------------------------------------
// Donut — the status breakdown (§14.8 screen 1)
// ---------------------------------------------------------------------------

const DONUT_SIZE = 160;
const DONUT_RADIUS = 60;
const DONUT_STROKE = 24;
const CIRCUMFERENCE = 2 * Math.PI * DONUT_RADIUS;

/**
 * One circle per slice, with a dash pattern and an offset, rather than arc
 * paths: the arithmetic is a length along a known circumference instead of
 * trigonometry, which is shorter and cannot go subtly wrong at the wrap-around.
 *
 * The centre carries the absolute total, because a donut without one
 * communicates ratios only — and "no appeals at all" is exactly the state a
 * staff member most needs told apart from "all clear".
 */
export function Donut({
  label,
  slices,
  centerValue,
  centerLabel,
  emptyLabel,
}: {
  label: string;
  slices: readonly Slice[];
  centerValue: string;
  centerLabel: string;
  emptyLabel: string;
}): React.JSX.Element {
  const sum = slices.reduce((acc, slice) => acc + slice.value, 0);
  if (sum === 0) return <EmptyChart message={emptyLabel} />;

  let consumed = 0;
  return (
    <ChartFrame label={label} className="flex flex-wrap items-center justify-center gap-6">
      <svg
        width={DONUT_SIZE}
        height={DONUT_SIZE}
        viewBox={`0 0 ${DONUT_SIZE} ${DONUT_SIZE}`}
        className="shrink-0"
      >
        <g transform={`rotate(-90 ${DONUT_SIZE / 2} ${DONUT_SIZE / 2})`}>
          <circle
            cx={DONUT_SIZE / 2}
            cy={DONUT_SIZE / 2}
            r={DONUT_RADIUS}
            fill="none"
            strokeWidth={DONUT_STROKE}
            className="stroke-surface-100"
          />
          {slices.map((slice, index) => {
            const length = (slice.value / sum) * CIRCUMFERENCE;
            const offset = -consumed;
            consumed += length;
            if (slice.value === 0) return null;
            return (
              <circle
                key={slice.key}
                cx={DONUT_SIZE / 2}
                cy={DONUT_SIZE / 2}
                r={DONUT_RADIUS}
                fill="none"
                strokeWidth={DONUT_STROKE}
                strokeDasharray={`${length} ${CIRCUMFERENCE - length}`}
                strokeDashoffset={offset}
                className={colorOf(slice, index).stroke}
              >
                <title>{`${slice.label}: ${slice.value}`}</title>
              </circle>
            );
          })}
        </g>
        <text
          x="50%"
          y="47%"
          textAnchor="middle"
          className="fill-ink-900 text-xl font-bold tabular-nums"
        >
          {centerValue}
        </text>
        <text x="50%" y="60%" textAnchor="middle" className="fill-ink-500 text-xs">
          {centerLabel}
        </text>
      </svg>

      {/* The legend is the chart's text equivalent, not decoration. */}
      <ul className="flex min-w-40 flex-col gap-1">
        {slices.map((slice, index) => (
          <li key={slice.key} className="flex items-center gap-2 text-sm">
            <span className={`h-3 w-3 shrink-0 rounded-chip ${colorOf(slice, index).bg}`} />
            <span className="mr-auto text-ink-700">{slice.label}</span>
            <span className="font-semibold tabular-nums text-ink-900">{slice.value}</span>
          </li>
        ))}
      </ul>
    </ChartFrame>
  );
}

// ---------------------------------------------------------------------------
// Sparkline — the received/closed trend (§14.8 screen 1)
// ---------------------------------------------------------------------------

const SPARK_WIDTH = 640;
const SPARK_HEIGHT = 120;

export interface SparkSeries {
  key: string;
  label: string;
  points: readonly number[];
  color: ChartColor;
}

/**
 * Two overlaid polylines on ONE shared scale.
 *
 * Shared deliberately: drawing "received" and "closed" against independent
 * y-axes makes a growing backlog look like a balance, which is the specific
 * misreading this chart exists to prevent. A series of zeros is drawn as a flat
 * line along the bottom rather than omitted — the endpoint zero-fills its
 * buckets (§14.2.3) so a quiet week is a zero, and the chart says so.
 */
export function Sparkline({
  label,
  series,
  emptyLabel,
}: {
  label: string;
  series: readonly SparkSeries[];
  emptyLabel: string;
}): React.JSX.Element {
  const length = Math.max(...series.map((line) => line.points.length), 0);
  if (length === 0) return <EmptyChart message={emptyLabel} />;

  const max = Math.max(...series.flatMap((line) => [...line.points]), 1);
  const step = length > 1 ? SPARK_WIDTH / (length - 1) : 0;

  const path = (points: readonly number[]): string =>
    points
      .map((value, index) => {
        const x = length > 1 ? index * step : SPARK_WIDTH / 2;
        const y = SPARK_HEIGHT - (value / max) * SPARK_HEIGHT;
        return `${index === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`;
      })
      .join(' ');

  return (
    <ChartFrame label={label} className="flex flex-col gap-3">
      <svg
        viewBox={`0 0 ${SPARK_WIDTH} ${SPARK_HEIGHT}`}
        preserveAspectRatio="none"
        className="h-32 w-full"
      >
        {series.map((line) => (
          <path
            key={line.key}
            d={path(line.points)}
            fill="none"
            strokeWidth={2}
            // Without this the non-uniform viewBox scaling stretches the stroke
            // into a wedge that is thick at one end of the chart and thin at
            // the other.
            vectorEffect="non-scaling-stroke"
            strokeLinejoin="round"
            className={line.color.stroke}
          >
            <title>{line.label}</title>
          </path>
        ))}
      </svg>
      <ul className="flex flex-wrap gap-4">
        {series.map((line) => (
          <li key={line.key} className="flex items-center gap-2 text-xs text-ink-500">
            <span className={`h-0.5 w-6 ${line.color.bg}`} />
            {line.label}
          </li>
        ))}
      </ul>
    </ChartFrame>
  );
}
